/**
 * Regional-brand additions to the equipment catalog.
 *
 * Five brands not covered by the existing committed catalogs nor by the
 * in-progress untracked catalogs as of 2026-05-15:
 *   • Reolink   — SOHO NVRs + PoE switches
 *   • HiLook    — Hikvision sub-brand NVRs (popular in PL/EU mid-tier)
 *   • Jablotron — CEE alarm market leader (CZ/SK/PL dominance)
 *   • Inim      — Italian alarm specialist (popular IT/EU)
 *   • Pyronix   — Hikvision-owned UK/EU intrusion specialist
 *
 * Every entry below is grounded in a primary manufacturer source (official
 * product page, datasheet PDF, or — where the manufacturer site returned
 * 403 / blocked WebFetch — the same spec text mirrored on an authorised
 * distributor that quotes the manufacturer datasheet verbatim). Each entry
 * carries a `datasheetUrl` and a `// SRC:` comment with the page actually
 * read. Where a field was absent from the source it is left undefined —
 * never guessed.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { DetectionSensorCatalogEntry } from './detectionSensorCatalog';
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

// ═══════════════════════════════════════════════════════════════════════════
// PART 1 — PIR motion detectors (Jablotron + Pyronix wireless PIRs).
// These use the DetectionSensorCatalogEntry shape (top-level rangeM / hfovDeg
// / mounting fields), not the AlarmPanelSpec-style nested `specs` block.
// ═══════════════════════════════════════════════════════════════════════════

export const REGIONAL_PIR_DETECTORS: DetectionSensorCatalogEntry[] = [
  // ────────────────────────────────────────────────────────────────────────
  // Jablotron — wireless wall PIRs (868.1 MHz Jablotron protocol)
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.jablotron.com/file/edee/qrdl/ja-150p/ja-150p_en_mmb51111.pdf
  //      JA-150P datasheet — 110° / 12 m standard lens, 2.5 m install height,
  //      2× AA, ~2 yr battery, 868.1 MHz, -10..+40 °C, EN 50130-4/EN 55022.
  {
    id: 'jablotron-ja-150p',
    manufacturer: 'Jablotron',
    model: 'JA-150P',
    pattern: 'cone',
    category: 'outdoor-wall-pir', // wall-mount (legacy category name)
    rangeM: 12,
    hfovDeg: 110,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-motion-pir-detector-259/',
    tagline:
      'Wireless wall PIR — 12 m / 110°, 868 MHz Jablotron protocol, 2-year AA',
  },

  // SRC: https://www.jablotron.com/en/produkt/wireless-pir-motion-detector-with-basic-pet-immunity-1248/
  //      JA-150P PET — same 12 m / 110° but with pet-immune lens.
  {
    id: 'jablotron-ja-150p-pet',
    manufacturer: 'Jablotron',
    model: 'JA-150P PET',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 110,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    operatingTempMinC: -10,
    operatingTempMaxC: 40,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-pir-motion-detector-with-basic-pet-immunity-1248/',
    tagline:
      'Wireless wall PIR with pet immunity — 12 m / 110°, 868 MHz, 2-year AA',
  },

  // SRC: https://www.jablotron.com/en/produkt/wireless-pir-motion-detector-with-90-photo-verification-camera-674/
  //      JA-160PC (90°) — wireless PIR with photo-verification camera, 90°/12 m
  //      guaranteed coverage, VGA colour photos, 868.1 MHz, 2× AA.
  {
    id: 'jablotron-ja-160pc-90',
    manufacturer: 'Jablotron',
    model: 'JA-160PC (90)',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.2,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-pir-motion-detector-with-90-photo-verification-camera-674/',
    tagline:
      'Wireless PIR + photo-verification camera — 12 m / 90°, VGA colour stills',
  },

  // ────────────────────────────────────────────────────────────────────────
  // Pyronix — Enforcer two-way wireless PIRs (868 MHz FM)
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.pyronix.com/products/kx10dtp-we
  //      KX10DTP-WE datasheet — dual-tech PIR+MW, 10 m / 85° volumetric lens
  //      (60 zones, 7 planes), pet immunity to 25 kg, install height 1.8–2.4 m,
  //      2× CR123A 3 V, ≥2 yr battery, Enforcer two-way 868 MHz.
  {
    id: 'pyronix-kx10dtp-we',
    manufacturer: 'Pyronix',
    model: 'KX10DTP-WE',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 10,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.1,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    batteryOperated: true,
    datasheetUrl: 'https://www.pyronix.com/products/kx10dtp-we',
    tagline:
      'Wireless dual-tech PIR+MW — 10 m / 85°, pet immune 25 kg, Enforcer 868 MHz',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// PART 2 — Everything else: NVRs, PoE switches, alarm panels, magnetic
// contacts, sirens, keypads. These use the BaseEntry + nested `specs` shape.
// ═══════════════════════════════════════════════════════════════════════════

export const REGIONAL_ADDITIONS_CATALOG: EquipmentCatalogEntry[] = [
  // ────────────────────────────────────────────────────────────────────────
  // REOLINK — NVRs (SOHO PoE recorders)
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://reolink.com/product/rln8-410/
  //      RLN8-410 — 12-channel (8 PoE + 4 Wi-Fi/battery), 1× SATA up to 8 TB,
  //      1 eSATA, H.264/H.265, supports up to 12 MP per channel, 10/100 PoE.
  {
    id: 'reolink-rln8-410',
    manufacturer: 'Reolink',
    model: 'RLN8-410',
    category: 'nvr-recorder',
    tagline:
      '8-PoE + 4-Wi-Fi NVR — 12 channels, 12 MP support, H.265, 1× SATA + eSATA',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rln8-410/',
    specs: {
      channels: 12,
      hddBays: 1,
      hddBayMaxTb: 8,
      poeInPorts: 8,
      maxResolution: '12 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
      networkPorts: '1× 10/100/1000 Mbps + 8× 10/100 PoE',
    },
  },

  // SRC: https://reolink.com/product/rln16-410/
  //      RLN16-410 — up to 24 IP cameras (max 16 wired PoE), 2× SATA, H.265,
  //      supports 12 MP per channel, 16 built-in PoE ports.
  {
    id: 'reolink-rln16-410',
    manufacturer: 'Reolink',
    model: 'RLN16-410',
    category: 'nvr-recorder',
    tagline:
      '16-PoE NVR — up to 24 IP cameras (16 wired), 12 MP support, 2× SATA, H.265',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rln16-410/',
    specs: {
      channels: 24,
      hddBays: 2,
      poeInPorts: 16,
      maxResolution: '12 MP',
      codecs: ['H.264', 'H.265'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // REOLINK — PoE switches
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://reolink.com/product/rla-ps1/
  //      RLA-PS1 — 10-port (8× 10/100 PoE+ + 2× 1 G uplink), 120 W total,
  //      30 W per port, IEEE 802.3af/at, metal housing.
  {
    id: 'reolink-rla-ps1',
    manufacturer: 'Reolink',
    model: 'RLA-PS1',
    category: 'poe-switch',
    tagline:
      '10-port PoE+ switch — 8× FE PoE + 2× GbE uplink, 120 W, 802.3af/at, unmanaged',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rla-ps1/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 120,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10/100/1000 Mbps RJ45',
      management: 'unmanaged',
      formFactor: 'desktop',
    },
  },

  // SRC: https://reolink.com/product/rla-ps1e/
  //      RLA-PS1E — 5-port (4× GbE PoE+ + 1× GbE uplink), 65 W total,
  //      30 W per port, IEEE 802.3af/at, fanless.
  {
    id: 'reolink-rla-ps1e',
    manufacturer: 'Reolink',
    model: 'RLA-PS1E',
    category: 'poe-switch',
    tagline:
      '5-port PoE+ switch — 4× GbE PoE + 1× GbE uplink, 65 W, fanless, unmanaged',
    verified: true,
    datasheetUrl: 'https://reolink.com/product/rla-ps1e/',
    specs: {
      totalPorts: 5,
      poePorts: 4,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 10/100/1000 Mbps RJ45',
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // HILOOK — Value NVRs (Hikvision sub-brand, 4 / 8 / 16-channel PoE)
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-104mh-c-4p/
  //      NVR-104MH-C/4P (D variant) — 4 IP channels, 4 PoE ports, 1× SATA up
  //      to 6 TB, decoding 1× 8 MP / 3× 4 MP / 6× 1080p, 4K HDMI + 1080 VGA,
  //      H.265+/H.265/H.264+/H.264.
  {
    id: 'hilook-nvr-104mh-c-4p',
    manufacturer: 'HiLook',
    model: 'NVR-104MH-C/4P (D)',
    category: 'nvr-recorder',
    tagline:
      '4-ch + 4 PoE NVR — 8 MP support, H.265+, 4K HDMI, 1× SATA 6 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-104mh-c-4p/',
    specs: {
      channels: 4,
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 4,
      maxResolution: '8 MP',
      codecs: ['H.264', 'H.265', 'H.265+'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // SRC: https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-108mh-c-8p/
  //      NVR-108MH-C/8P — 8 IP channels, 8 PoE (802.3af/at), 80 Mbps in/out,
  //      decoding 1× 8 MP / 3× 4 MP / 6× 1080p, 1× SATA up to 6 TB, 4K HDMI.
  {
    id: 'hilook-nvr-108mh-c-8p',
    manufacturer: 'HiLook',
    model: 'NVR-108MH-C/8P (D)',
    category: 'nvr-recorder',
    tagline:
      '8-ch + 8 PoE NVR — 80 Mbps, 8 MP support, H.265+, 4K HDMI, 1× SATA 6 TB',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-108mh-c-8p/',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 8,
      maxResolution: '8 MP',
      codecs: ['H.264', 'H.265', 'H.265+'],
      onvif: true,
      formFactor: 'desktop',
    },
  },

  // SRC: https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-216mh-c-16p/
  //      NVR-216MH-C/16P — 16 channels, 16 PoE, 160 Mbps incoming, 2× SATA up
  //      to 8 TB each, decoding 1× 8 MP / 5× 1080p, 4K HDMI, 1U rack.
  {
    id: 'hilook-nvr-216mh-c-16p',
    manufacturer: 'HiLook',
    model: 'NVR-216MH-C/16P (D)',
    category: 'nvr-recorder',
    tagline:
      '16-ch + 16 PoE NVR — 160 Mbps in, 8 MP support, 2× SATA 8 TB, 1U rack',
    verified: true,
    datasheetUrl:
      'https://www.hikvision.com/en/products/HiLook-IP-Products/Network-Video-Recorders/Value-NVR/nvr-216mh-c-16p/',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      hddBays: 2,
      hddBayMaxTb: 8,
      poeInPorts: 16,
      maxResolution: '8 MP',
      codecs: ['H.264', 'H.265', 'H.265+'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 Mbps + 16× independent PoE',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // JABLOTRON — JABLOTRON 100+ control panels
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.jablotron.com/en/produkt/control-panel-with-lan-communicator-1235/
  //      JA-103K — up to 50 BUS/wireless zones, 8 sections, 50 user codes,
  //      32 programmable outputs, built-in LAN, EN 50131-1 Grade 2.
  {
    id: 'jablotron-ja-103k',
    manufacturer: 'Jablotron',
    model: 'JA-103K',
    category: 'alarm-panel',
    tagline:
      'JABLOTRON 100+ hybrid panel — 50 zones, 8 sections, built-in LAN, Grade 2',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/control-panel-with-lan-communicator-1235/',
    specs: {
      series: 'JABLOTRON 100+',
      zonesOnboard: 0,
      zonesMax: 50,
      partitions: 8,
      outputsMax: 32,
      users: 50,
      grade: 2,
      communicators: ['Ethernet'],
      mobileApp: 'MyJABLOTRON',
    },
  },

  // SRC: https://www.jablotron.com/en/produkt/control-panel-with-built-in-gsm-gprs-lan-communicator-271/
  //      JA-106K — up to 120 BUS/wireless zones, 15 sections, 300 user codes,
  //      32 programmable outputs, built-in GSM/GPRS + LAN, Grade 2.
  {
    id: 'jablotron-ja-106k',
    manufacturer: 'Jablotron',
    model: 'JA-106K',
    category: 'alarm-panel',
    tagline:
      'JABLOTRON 100+ panel — 120 zones, 15 sections, built-in LAN + GSM/GPRS',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/control-panel-with-built-in-gsm-gprs-lan-communicator-271/',
    specs: {
      series: 'JABLOTRON 100+',
      zonesOnboard: 0,
      zonesMax: 120,
      partitions: 15,
      outputsMax: 32,
      users: 300,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'MyJABLOTRON',
    },
  },

  // SRC: https://www.jablotron.com/en/produkt/gsm-control-panel-with-lan-communicator-1237/
  //      JA-107K — up to 230 zones (120 wireless), 15 sections, 600 user codes,
  //      128 programmable outputs (32 wireless), built-in LAN (GSM/radio
  //      optional via JA-107KR/KRY variants), 5 ARC's, Grade 2.
  {
    id: 'jablotron-ja-107k',
    manufacturer: 'Jablotron',
    model: 'JA-107K',
    category: 'alarm-panel',
    tagline:
      'JABLOTRON 100+ commercial panel — 230 zones, 15 sections, 600 users, dual BUS',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/gsm-control-panel-with-lan-communicator-1237/',
    specs: {
      series: 'JABLOTRON 100+',
      zonesOnboard: 0,
      zonesMax: 230,
      partitions: 15,
      outputsMax: 128,
      users: 600,
      grade: 2,
      communicators: ['Ethernet'],
      mobileApp: 'MyJABLOTRON',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // JABLOTRON — peripherals (magnetic contact, keypad, sirens)
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.jablotron.com/en/produkt/wireless-magnetic-detector-with-2-universal-inputs-342/
  //      JA-150M — wireless magnetic detector with 2 universal inputs,
  //      EN 50131-1 Grade 2, 868.1 MHz, 1× AA, ~2 yr battery, 109×24×22 mm.
  {
    id: 'jablotron-ja-150m',
    manufacturer: 'Jablotron',
    model: 'JA-150M',
    category: 'magnetic-contact',
    tagline:
      'Wireless magnetic contact + 2 universal inputs — 868 MHz, Grade 2, 2-yr AA',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-magnetic-detector-with-2-universal-inputs-342/',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '109x24x22',
    },
  },

  // SRC: https://www.jablotron.com/en/produkt/wireless-access-module-with-rfid-and-keypad-anthracite-2023/
  //      JA-153E — two-way wireless keypad with RFID, segment LCD-style icons,
  //      868 MHz Jablotron 2-way, EN 50131-1/3/6 Grade 2, 2× AA ~2 yr,
  //      up to 200 m open-area radio range, supports up to 20 JA-192E control
  //      segments.
  {
    id: 'jablotron-ja-153e',
    manufacturer: 'Jablotron',
    model: 'JA-153E',
    category: 'keypad',
    tagline:
      'Wireless RFID + segment-keypad module — 868 MHz, 2-way, Grade 2, 20 segments',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-access-module-with-rfid-and-keypad-anthracite-2023/',
    specs: {
      display: 'led', // segment-icon style, not pixel LCD
      connection: 'Jablotron 868 MHz two-way',
      readerType: 'em-marin-125khz', // RFID 125 kHz on Jablotron 100+
      compatibility: ['JABLOTRON 100+'],
      grade: 2,
    },
  },

  // SRC: https://www.jablotron.com/en/produkt/bus-external-siren-202/
  //      JA-111A — BUS wired outdoor siren, piezo 110 dB/1 m, IP34D, Grade 2
  //      EN 50131-1/-4, environmental class IV, -25..+60 °C, NiCd 4.8 V /
  //      1800 mAh backup, addressable on Jablotron BUS.
  {
    id: 'jablotron-ja-111a',
    manufacturer: 'Jablotron',
    model: 'JA-111A',
    category: 'outdoor-siren',
    tagline:
      'BUS wired outdoor siren — piezo 110 dB, IP34D, Grade 2, NiCd backup',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/bus-external-siren-202/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 110,
      grade: 2,
      batteryBackup: 'built-in',
      ipRating: 'IP34D',
    },
  },

  // SRC: https://www.jablotron.com/en/produkt/wireless-outdoor-siren-with-lithium-battery-225/
  //      JA-180A — fully wireless outdoor siren, 112 dB/1 m, Grade 2
  //      EN 50131-1/-4/-5-3, 868.1 MHz Jablotron protocol, lithium battery,
  //      IP34D, -25..+60 °C, max audible alarm 3 min.
  {
    id: 'jablotron-ja-180a',
    manufacturer: 'Jablotron',
    model: 'JA-180A',
    category: 'outdoor-siren',
    tagline:
      'Wireless outdoor siren — 112 dB, 868 MHz, IP34D, Grade 2, lithium backup',
    verified: true,
    datasheetUrl:
      'https://www.jablotron.com/en/produkt/wireless-outdoor-siren-with-lithium-battery-225/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 112,
      grade: 2,
      batteryBackup: 'built-in',
      ipRating: 'IP34D',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // INIM — SmartLiving hybrid control panels
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-515
  //      SmartLiving 515 — 5 to 15 terminals (zones/outputs assignable),
  //      5 partitions, 1.2 A PSU, optional GSM/GPRS + TCP/IP plug-in modules,
  //      EN 50131-3 Grade 3 / EN 50131-6 Grade 2.
  {
    id: 'inim-smartliving-515',
    manufacturer: 'Inim',
    model: 'SmartLiving 515',
    category: 'alarm-panel',
    tagline:
      'SmartLiving hybrid panel — 15 terminals, 5 partitions, Grade 3, IP/GSM optional',
    verified: true,
    datasheetUrl:
      'https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-515',
    specs: {
      series: 'SmartLiving',
      zonesOnboard: 5,
      zonesMax: 15,
      partitions: 5,
      grade: 3,
      psuAmps: 1.2,
      mobileApp: 'Inim Home',
    },
  },

  // SRC: https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-10501050g3
  //      SmartLiving 1050 — 10 to 50 terminals, 10 partitions, 3 A PSU, single
  //      12 V / 7 Ah battery, EN 50131-6 Grade 3, optional GSM/GPRS + TCP/IP.
  {
    id: 'inim-smartliving-1050',
    manufacturer: 'Inim',
    model: 'SmartLiving 1050',
    category: 'alarm-panel',
    tagline:
      'SmartLiving panel — 50 terminals, 10 partitions, Grade 3, 3 A PSU',
    verified: true,
    datasheetUrl:
      'https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-10501050g3',
    specs: {
      series: 'SmartLiving',
      zonesOnboard: 10,
      zonesMax: 50,
      partitions: 10,
      grade: 3,
      psuAmps: 3,
      mobileApp: 'Inim Home',
    },
  },

  // SRC: https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-10100l10100lg3
  //      SmartLiving 10100L — 10 to 100 terminals, 15 partitions, 3 A PSU,
  //      space for 2× 17 Ah batteries, 100 user codes, 250 prox tags, RS232,
  //      PSTN, plug-in IP module, max 15 keypads + 30 Flex5 expanders.
  //      EN 50131-6 Grade 3.
  {
    id: 'inim-smartliving-10100l',
    manufacturer: 'Inim',
    model: 'SmartLiving 10100L',
    category: 'alarm-panel',
    tagline:
      'SmartLiving commercial panel — 100 terminals, 15 partitions, 100 users, Grade 3',
    verified: true,
    datasheetUrl:
      'https://www.inim.biz/en/security-control-panels/intrusion-detection-and-home-automation/smartliving-control-panels/smartliving-10100l10100lg3',
    specs: {
      series: 'SmartLiving',
      zonesOnboard: 10,
      zonesMax: 100,
      partitions: 15,
      users: 100,
      grade: 3,
      communicators: ['PSTN'],
      psuAmps: 3,
      mobileApp: 'Inim Home',
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // INIM — Air2 wireless peripherals
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.gam-service.it/images/pdf/INMAIR2MC100B.pdf (Air2-MC100
  //      manufacturer datasheet mirror, read 2026-05-15). Bidirectional
  //      wireless magnetic contact, 2 individually programmable I/O,
  //      36×95×26 mm, 130 g, lithium CR123A 3 V, ~4 yr battery, anti-opening
  //      + anti-tamper, 868 MHz Air2.
  {
    id: 'inim-air2-mc100',
    manufacturer: 'Inim',
    model: 'Air2-MC100',
    category: 'magnetic-contact',
    tagline:
      'Bidirectional wireless magnetic contact — 2 I/O, 868 MHz Air2, ~4-yr CR123A',
    verified: true,
    datasheetUrl: 'https://www.gam-service.it/images/pdf/INMAIR2MC100B.pdf',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '36x95x26',
    },
  },

  // SRC: https://www.inim.biz/en/intrusion-detection-and-home-automation/keypads/ariaw
  //      Air2-Aria/W — wireless keypad with graphic backlit LCD + 4 function
  //      keys, accelerometer (anti-tamper + wakeup), brightness sensor,
  //      auto-shutdown out of range, max 4 per Air2-BS200 transceiver,
  //      868 MHz Air2 two-way, manages SmartLiving system.
  {
    id: 'inim-air2-aria-w',
    manufacturer: 'Inim',
    model: 'Air2-Aria/W',
    category: 'keypad',
    tagline:
      'Wireless graphic-LCD keypad — 868 MHz Air2 two-way, accelerometer + brightness sensor',
    verified: true,
    datasheetUrl:
      'https://www.inim.biz/en/intrusion-detection-and-home-automation/keypads/ariaw',
    specs: {
      display: 'lcd-text',
      connection: 'Inim Air2 868 MHz two-way',
      compatibility: ['SmartLiving'],
    },
  },

  // ────────────────────────────────────────────────────────────────────────
  // PYRONIX — Enforcer wireless control panel
  // ────────────────────────────────────────────────────────────────────────

  // SRC: https://www.pyronix.com/uk/products/enforcer-v11
  //      Enforcer V11 (ENF32UK-WE) — 32 wireless zones expandable to 64 +
  //      2 wired expandable to 34 (total 66 zones), 4 areas, built-in keypad
  //      + proximity tag reader + Wi-Fi (HomeControl+ cloud), 80 user codes,
  //      32 wireless keyfobs, 3 programmable wired outputs expandable to 38,
  //      Enforcer 868 MHz FM two-way wireless.
  {
    id: 'pyronix-enforcer-v11',
    manufacturer: 'Pyronix',
    model: 'Enforcer V11 (ENF32UK-WE)',
    category: 'alarm-panel',
    tagline:
      'Enforcer wireless panel — 32+2 zones (exp 66), 4 areas, built-in keypad/RFID/Wi-Fi',
    verified: true,
    datasheetUrl: 'https://www.pyronix.com/uk/products/enforcer-v11',
    specs: {
      series: 'Enforcer',
      zonesOnboard: 2, // 2 wired onboard, 32 wireless via built-in transceiver
      zonesMax: 66,
      partitions: 4, // Pyronix terms these "areas"
      outputsOnboard: 3,
      outputsMax: 38,
      users: 80,
      grade: 2,
      communicators: ['Ethernet', 'wireless'], // built-in Wi-Fi for HomeControl+
      mobileApp: 'HomeControl+',
    },
  },
];
